import { useEffect, useMemo, useState } from "react";
import MSINav from "./MSINav";
import {
  moveWatchlistToPositions,
  storageGet,
  storageSet,
} from "./msiWatchlistManager";

import {
  addAlerts,
  buildAlertFromWatchlist,
  alertText,
} from "./msiAlertManager";

const panel = {
  background: "#171717",
  border: "1px solid #2A2A2A",
  borderRadius: 12,
  padding: 18,
};

const cellClip = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

function fmt(n, d = 1) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "-";
  return Number(n).toFixed(d);
}

function fmtDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString([], {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function biasColor(bias) {
  if (bias === "CALL") return "#22c55e";
  if (bias === "PUT") return "#ef4444";
  return "#f59e0b";
}

function playColor(label) {
  if (label === "A+" || label === "A") return "#22c55e";
  if (label === "B+" || label === "B") return "#f59e0b";
  return "#A0A0A0";
}

function toolbarBtn(enabled = true) {
  return {
    background: enabled ? "#262626" : "#171717",
    color: "white",
    border: "1px solid #3A3A3A",
    borderRadius: 8,
    padding: "9px 12px",
    fontWeight: 800,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.55,
  };
}

function greenBtn(enabled) {
  return {
    background: enabled ? "#16a34a" : "#262626",
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "8px 10px",
    fontWeight: 900,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.55,
    whiteSpace: "nowrap",
  };
}

function dangerBtn(enabled) {
  return {
    background: enabled ? "#ef4444" : "#262626",
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "8px 10px",
    fontWeight: 900,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.55,
    whiteSpace: "nowrap",
  };
}

export default function MSIWatchlist() {
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState({});
    const [q, setQ] = useState("");
    const [message, setMessage] = useState("");

  useEffect(() => {
    setItems(storageGet("msi_watchlist", []));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toUpperCase();
    if (!needle) return items;
    return items.filter(
      (x) =>
        String(x.ticker || "").toUpperCase().includes(needle) ||
        String(x.sector || "").toUpperCase().includes(needle) ||
        String(x.contract || "").toUpperCase().includes(needle) ||
        String(x.target || "").toUpperCase().includes(needle)
    );
  }, [items, q]);

  const selectedItems = filtered.filter((x) => selected[x.id]);
  const callCount = items.filter((x) => x.bias === "CALL").length;
  const putCount = items.filter((x) => x.bias === "PUT").length;
  const avgMsi = items.length
    ? items.reduce((sum, x) => sum + Number(x.msi_score || 0), 0) / items.length
    : 0;

    function showMessage(text) {
      setMessage(text);

      setTimeout(() => {
        setMessage("");
      }, 2500);
    }

  function toggleSelected(id) {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function selectVisible() {
    const next = {};
    filtered.forEach((x) => {
      next[x.id] = true;
    });
    setSelected(next);
  }

  function clearSelected() {
    setSelected({});
  }

  function removeSelected() {
    if (!selectedItems.length) return;
    const selectedIds = new Set(selectedItems.map((x) => x.id));
    const next = items.filter((x) => !selectedIds.has(x.id));
    storageSet("msi_watchlist", next);
    setItems(next);
    clearSelected();
    showMessage(`Removed ${selectedItems.length} watchlist item(s).`);
  }

  function moveSelectedToPositions() {
    if (!selectedItems.length) {
      alert("Select at least one watchlist item first");
      return;
    }

    const result = moveWatchlistToPositions(selectedItems);
    setItems(result.watchlist);
    clearSelected();
    showMessage(
  `Moved ${result.added} to Positions. Skipped ${result.skipped} duplicate(s).`
);
  }

  function generateSelectedAlerts(copyAfterCreate = false) {
      if (!selectedItems.length) {
        alert("Select at least one watchlist item first");
        return;
    }

  const alerts = selectedItems.map((item) =>
    buildAlertFromWatchlist(
      item,
      item.bias === "PUT"
        ? "PUT WATCH"
        : "BUY WATCH"
    )
  );

  addAlerts(alerts);

  if (copyAfterCreate) {
    navigator.clipboard.writeText(
      alerts.map(alertText).join("\n\n----------------\n\n")
    );

    showMessage(
      `Generated and copied ${alerts.length} alert(s).`
    );
  } else {
    showMessage(
      `Generated ${alerts.length} alert(s).`
    );
  }

  clearSelected();
}


  const grid = "44px 78px 82px 86px 122px 65px 72px 96px 150px 120px 105px 120px";
  const minWidth = 1290;

  return (
    <div style={{ minHeight: "100vh", background: "#0B0B0B", color: "white", padding: 24 }}>
      <h1 style={{ fontSize: 40, marginBottom: 4 }}>MSI Watchlist</h1>
      <div style={{ color: "#A0A0A0", marginBottom: 16 }}>
        Plays you are monitoring before entry.
      </div>

      <MSINav />
      {message && (
          <div
            style={{
              ...panel,
              borderColor: "#2563eb",
              marginBottom: 14,
              color: "#D4D4D4",
              fontWeight: 800,
            }}
          >
            ✓ {message}
          </div>
        )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(160px, 1fr))", gap: 14, marginBottom: 18 }}>
        <div style={panel}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Watching</div><div style={{ fontSize: 32, fontWeight: 900 }}>{items.length}</div></div>
        <div style={panel}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Calls</div><div style={{ fontSize: 32, fontWeight: 900, color: "#22c55e" }}>{callCount}</div></div>
        <div style={panel}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Puts</div><div style={{ fontSize: 32, fontWeight: 900, color: "#ef4444" }}>{putCount}</div></div>
        <div style={panel}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Avg MSI</div><div style={{ fontSize: 32, fontWeight: 900 }}>{fmt(avgMsi, 0)}</div></div>
      </div>

      <div style={{ ...panel, marginBottom: 14, display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search watchlist..."
          style={{
            background: "#0B0B0B",
            border: "1px solid #2A2A2A",
            color: "white",
            borderRadius: 8,
            padding: "10px 12px",
            minWidth: 260,
          }}
        />

        <div style={{ color: "#A0A0A0", fontWeight: 800 }}>
          Selected: <span style={{ color: "white" }}>{selectedItems.length}</span>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={selectVisible} style={toolbarBtn()}>
          Select Visible
        </button>

        <button onClick={clearSelected} style={toolbarBtn()}>
          Clear
        </button>

        <button
          onClick={() => generateSelectedAlerts(false)}
          disabled={!selectedItems.length}
          style={toolbarBtn(!!selectedItems.length)}
        >
          Generate Alerts
        </button>

        <button
          onClick={() => generateSelectedAlerts(true)}
          disabled={!selectedItems.length}
          style={toolbarBtn(!!selectedItems.length)}
        >
          Copy Alerts
        </button>

        <button
          onClick={moveSelectedToPositions}
          disabled={!selectedItems.length}
          style={greenBtn(!!selectedItems.length)}
        >
          Move To Positions
        </button>

        <button
          onClick={removeSelected}
          disabled={!selectedItems.length}
          style={dangerBtn(!!selectedItems.length)}
        >
          Remove Selected
        </button>
        </div>
      </div>

      <div style={{ ...panel, overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: grid, minWidth, gap: 12, color: "#A0A0A0", fontWeight: 800, borderBottom: "1px solid #2A2A2A", paddingBottom: 10, marginBottom: 8, fontSize: 14 }}>
          <div></div><div>Ticker</div><div>Bias</div><div>Play</div><div>Contract</div><div>MSI</div><div>Opp</div><div>Conf</div><div>Target</div><div>Structure</div><div>Sector</div><div>Added</div>
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: 24, color: "#A0A0A0" }}>
            No watchlist items yet. Add plays from MSI Universe.
          </div>
        )}

        {filtered.map((item) => (
          <div key={item.id} style={{ display: "grid", gridTemplateColumns: grid, minWidth, gap: 12, alignItems: "center", padding: "10px 0", borderBottom: "1px solid #242424", fontSize: 14 }}>
            <div><input type="checkbox" checked={!!selected[item.id]} onChange={() => toggleSelected(item.id)} style={{ transform: "scale(1.05)", cursor: "pointer" }} /></div>
            <div style={{ fontWeight: 900 }}>{item.ticker}</div>
            <div><span style={{ background: biasColor(item.bias), color: "white", borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 900 }}>{item.bias}</span></div>
            <div style={{ color: playColor(item.play_label), fontWeight: 900 }}>{item.play_label} · {item.play_score}</div>
            <div style={{ fontWeight: 800, ...cellClip }}>{item.contract}</div>
            <div>{Math.round(Number(item.msi_score || 0))}</div>
            <div style={{ color: item.bias === "PUT" ? "#ef4444" : "#22c55e", fontWeight: 900 }}>
              {item.bias === "PUT" ? "-" : "+"}{fmt(Math.abs(Number(item.opportunity || 0)), 1)}%
            </div>
            <div>{Math.round(Number(item.confluence || 0))}</div>
            <div title={item.target} style={{ color: "#A0A0A0", ...cellClip }}>{item.target}</div>
            <div style={cellClip}>{item.structure || "-"}</div>
            <div style={{ color: "#D4D4D4", ...cellClip }}>{item.sector || "-"}</div>
            <div style={{ color: "#A0A0A0" }}>{fmtDate(item.added_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
